import {
  GetCPGCallBackLogsRequest,
  GetCPGCallBackLogsResponse,
} from "@services/models/Transactions/GetCPGCallBackLogsModel";
import {
  GetCPGTransactionDetailsResponse,
  GetCPGTransactionListRequest,
  GetCPGTransactionListResponse,
} from "@services/models/Transactions/GetCPGTransactionListModel";
import { GetNetworkListResponse } from "@services/models/Transactions/GetNetworkList";
import {
  GetP2PTradeDetailsRequest,
  GetP2PTradeDetailsResponse,
} from "@services/models/Transactions/GetP2PTradeDetailsModel";
import {
  GetP2PTradeListRequest,
  GetP2PTradeListResponse,
} from "@services/models/Transactions/GetP2PTradeListModel";
import { GetTokenListResponse } from "@services/models/Transactions/GetTokenList";
import { GetTransactionPayloadCounterpartyResponse } from "@services/models/Transactions/GetTransactionPayloadCounterparty";
import { GetTravelRuleCounterpartyResponse } from "@services/models/Transactions/GetTravelRuleCounterparty";
import {
  GetUnifiedTransactionDetailsRequest,
  GetUnifiedTransactionDetailsResponse,
} from "@services/models/Transactions/GetUnifiedTransactionDetailsModel";
import {
  ClearComplianceResponse,
  GetUnifiedTransactionsRequest,
  GetUnifiedTransactionsResponse,
} from "@services/models/Transactions/GetUnifiedTransactionsModel";
import {
  SettleTransactionRequest,
  SettleTransactionResponse,
} from "@services/models/Transactions/SettleTransactionModel";
import {
  UpdateP2PEventTradeDeadlineRequest,
  UpdateP2PEventTradeDeadlineResponse,
} from "@services/models/Transactions/UpdateP2PEventTradeDeadline";
import {
  ValidateTransactionDetialsRequest,
  ValidateTransactionDetialsResponse,
} from "@services/models/Transactions/ValidateTransactionDetails";
import { createApiClient } from "../api/createApiClient";
import { AxiosInstance } from "axios";

class TransactionApiService {
  private api: AxiosInstance;
  constructor() {
    this.api = createApiClient();
  }

  public async getP2PTradeList(
    data: GetP2PTradeListRequest,
  ): Promise<GetP2PTradeListResponse> {
    try {
      const response = await this.api.get(`/adminPanel/getP2PTradeList`, {
        params: {
          rows: data.rows,
          page: data.page,
          tradeStatus: data.tradeStatus || undefined,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async searchPrimeTrade(
    searchTerm: string,
  ): Promise<GetP2PTradeListResponse> {
    try {
      const response = await this.api.get(`/adminPanel/searchP2PTrade`, {
        params: {
          searchTerm: searchTerm,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getP2PTradeDetails(
    data: GetP2PTradeDetailsRequest,
  ): Promise<GetP2PTradeDetailsResponse> {
    try {
      const response = await this.api.get(`/adminPanel/getP2PTradeDetails`, {
        params: {
          tradeId: data.tradeId,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getCPGTransactionList(
    data: GetCPGTransactionListRequest,
  ): Promise<GetCPGTransactionListResponse> {
    try {
      const response = await this.api.get("/adminPanel/getCPGTransactionList", {
        params: {
          rows: data.rows,
          page: data.page,
          status: data.status,
          isPaying: data.isPaying,
          isPayout: data.isPayout,
          searchValue: data.searchValue,
          search: true,
          compliancePending: data.compliancePending,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getCPGTransactionDetails(
    data: GetCPGTransactionListRequest,
  ): Promise<GetCPGTransactionDetailsResponse> {
    try {
      const response = await this.api.get(
        "/adminPanel/getCPGTransactionDetails",
        {
          params: {
            transactionId: data.transactionId,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async settleTransaction(
    data: SettleTransactionRequest,
    endPoint: string = "/adminPanel/settleTransaction",
  ): Promise<SettleTransactionResponse> {
    try {
      const response = await this.api.post(endPoint, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async updateP2PEventTradeDeadline(
    data: UpdateP2PEventTradeDeadlineRequest,
  ): Promise<UpdateP2PEventTradeDeadlineResponse> {
    try {
      const response = await this.api.post(
        "/adminPanel/updateP2PTradeEventDeadline",
        data,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getTokenList(): Promise<GetTokenListResponse> {
    try {
      const response = await this.api.get<GetTokenListResponse>(
        "/missing-transactions/tokens",
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async validateTransactionDetails(
    data: ValidateTransactionDetialsRequest,
  ): Promise<ValidateTransactionDetialsResponse> {
    try {
      const response = await this.api.get<ValidateTransactionDetialsResponse>(
        "/missing-transactions/search",
        {
          params: data,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getNetworksList(): Promise<GetNetworkListResponse> {
    try {
      const response = await this.api.get<GetNetworkListResponse>(
        "/transactions/getNetworksList",
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getCPGCallbackLogs(
    data: GetCPGCallBackLogsRequest,
  ): Promise<GetCPGCallBackLogsResponse> {
    try {
      const response = await this.api.get(`/adminPanel/getCPGCallbackLogs`, {
        params: {
          transactionId: data.transactionId,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getUnifiedTransactions(
    data: GetUnifiedTransactionsRequest,
  ): Promise<GetUnifiedTransactionsResponse> {
    try {
      const response = await this.api.get(
        `/adminPanel/getUnifiedTransactions`,
        {
          params: {
            days: data.days,
            startDate: data.startDate,
            endDate: data.endDate,
            page: data.page,
            limit: data.limit,
            transactionType: data.transactionType,
            statusId: data.statusId,
            search: data.search,
            compliancePending: data.compliancePending,
            userAccountType: data.userAccountType || undefined,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getTravelRuleCounterparty(
    transactionId: number,
  ): Promise<GetTravelRuleCounterpartyResponse> {
    try {
      const response = await this.api.get<GetTravelRuleCounterpartyResponse>(
        "/adminPanel/getTravelRuleCounterparty",
        {
          params: { transactionId },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getTransactionPayloadCounterparty(
    merchantOrderId: string,
  ): Promise<GetTransactionPayloadCounterpartyResponse> {
    try {
      const response =
        await this.api.get<GetTransactionPayloadCounterpartyResponse>(
          "/adminPanel/getTransactionPayloadCounterparty",
          {
            params: { merchantOrderId },
          },
        );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async clearCompliance(
    transactionId: number,
  ): Promise<ClearComplianceResponse> {
    try {
      const response = await this.api.post<ClearComplianceResponse>(
        "/adminPanel/clearCompliance",
        {},
        { params: { transactionId } },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getUnifiedTransactionDetails(
    data: GetUnifiedTransactionDetailsRequest,
  ): Promise<GetUnifiedTransactionDetailsResponse> {
    try {
      const response = await this.api.get(
        `/adminPanel/getUnifiedTransactionDetails`,
        {
          params: {
            transactionId: data.transactionId,
            merchantId: data.merchantId,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export const transactionApiServices = new TransactionApiService();
